import { memo } from 'react';
import { useRef } from 'react';
import { Vector2Tuple } from 'three';

import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { FShallowNode, FShallowNodeTypesEnum } from '@spazious/dollhouse-shallow-graph';
import { useStaging, useStore } from '@spazious/dollhouse-store';
import { ToolModeEnum } from '@spazious/dollhouse-types';
import { useEventCallback } from '@spazious/react-hooks';

import { DrawPolygonEvent, PolygonDrawControls } from '../PolygonDrawControls/index.js';

export const FAreaControls = memo(() => {
  const tool = useStore(({ tool }) => tool);
  const { staging, createAction } = useStaging();
  const set = useStore(({ set }) => set);
  const selector = useRef<string | null>(null);

  const applyPolygonPoints = (points: Vector2Tuple[]) => {
    const actions = [];
    actions.push(createAction('update', selector.current!, 'points', points));
    return actions;
  };

  const onDrawPolygon = useEventCallback((event: DrawPolygonEvent) => {
    const { points, originalEvent, end = false } = event;

    if (end) {
      if (selector.current) {
        const actions = [];
        if (points.length > 2) {
          actions.push(...applyPolygonPoints(points));
        } else {
          // not enough points, remove node
          actions.push(createAction('remove', selector.current));
          selector.current = null;
        }

        staging.add(actions);
      }
      if (staging.stack.length) {
        staging.commit(CommanderActionTypesEnum.Add);
      }
      set({
        showNavMesh: false,
        tool: ToolModeEnum.None,
      });
      selector.current = null;
      return;
    }

    switch (originalEvent.type) {
      case 'pointerdown':
        if (points.length === 1) {
          // starting the polygon
          staging.prepare(true);
          const action = createAction('add', {
            type: FShallowNodeTypesEnum.FArea,
            points: [],
          } as FShallowNode);
          staging.add(action);
          selector.current = action.payload.selector;
        }

        if (selector.current && points.length > 0) {
          staging.add(applyPolygonPoints(points));
        }
        break;

      case 'pointermove':
        if (!selector.current) {
          return;
        }
        staging.add(applyPolygonPoints(points));
        break;

      case 'pointerup':
        break;

      default:
        break;
    }
  });

  return tool === ToolModeEnum.FArea ? <PolygonDrawControls onDraw={onDrawPolygon} /> : null;
});
